const HEX_COLOR = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const THEME_KEYS = [
  "textColor",
  "bgColor",
  "primaryColor",
  "buttonTextColor",
  "logo",
  "secondaryColor",
  "secondaryTextColor",
  "headingColor",
  "linkColor",
  "surfaceColor",
  "surfaceMutedColor",
  "borderColor",
  "borderRadius",
  "fontFamily",
];

const DEFAULT_THEME = {
  textColor: "#26273b",
  bgColor: "#ffffff",
  primaryColor: "#ccb17b",
  buttonTextColor: "#ffffff",
  secondaryColor: "#e9dcc0",
  secondaryTextColor: "#ccb17b",
  headingColor: "#26273b",
  linkColor: "#ccb17b",
  surfaceColor: "#ffffff",
  surfaceMutedColor: "#f0f0f5",
  borderColor: "#d6d6e1",
  borderRadius: 16,
};

const DEFAULT_FONT_FAMILIES = new Set([
  "roc-grotesk",
  "roc-grotesk, system-ui, sans-serif",
  "\"roc-grotesk\", system-ui, -apple-system, \"segoe ui\", roboto, sans-serif",
]);

const normalizeHex = (value) => {
  if (value == null || value === "") return null;
  const match = String(value).trim().match(HEX_COLOR);
  if (!match) return null;
  const hex = match[1];
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  return `#${full.toLowerCase()}`;
};

const normalizeLogo = (logo) => {
  if (logo == null || logo === "") return null;
  const value = String(logo).trim();
  return value || null;
};

const pickColor = (settings, key) => normalizeHex(settings[key]);

/** API / JWT hem camelCase hem snake_case gönderebilir */
const pickThemeColor = (settings, camelKey, snakeKey) =>
  pickColor(settings, camelKey) || pickColor(settings, snakeKey);

const SETTINGS_KEY_ALIASES = {
  text_color: "textColor",
  background_color: "bgColor",
  primary_color: "primaryColor",
  button_text_color: "buttonTextColor",
  secondary_color: "secondaryColor",
  secondary_text_color: "secondaryTextColor",
  heading_color: "headingColor",
  link_color: "linkColor",
  surface_color: "surfaceColor",
  surface_muted_color: "surfaceMutedColor",
  border_color: "borderColor",
  border_radius: "borderRadius",
  font_family: "fontFamily",
};

const normalizeSettingsKeys = (settings) => {
  if (!settings || typeof settings !== "object") return settings;
  const out = { ...settings };
  Object.entries(SETTINGS_KEY_ALIASES).forEach(([snake, camel]) => {
    if (hasThemeValue(out[snake]) && !hasThemeValue(out[camel])) {
      out[camel] = out[snake];
    }
  });
  return out;
};

const pickNumber = (settings, key) => {
  const value = settings[key];
  if (value == null || value === "") return null;
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

const pickString = (settings, key) => {
  const value = settings[key];
  if (value == null) return null;
  const text = String(value).trim();
  return text || null;
};

const isDefaultFontFamily = (value) => {
  if (!value) return true;
  return DEFAULT_FONT_FAMILIES.has(value.trim().toLowerCase());
};

const hasThemeValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string") return value.trim().length > 0;
  return false;
};

const pickThemeSettings = (settings) => {
  if (!settings || typeof settings !== "object") return null;
  settings = normalizeSettingsKeys(settings);
  const picked = {};
  THEME_KEYS.forEach((key) => {
    const value = settings[key];
    if (hasThemeValue(value)) {
      picked[key] = value;
    }
  });
  return Object.keys(picked).length ? picked : null;
};

const mergeComponentSections = (base = {}, patch = {}) => {
  const keys = new Set([...Object.keys(base), ...Object.keys(patch)]);
  const out = {};
  keys.forEach((key) => {
    out[key] = { ...(base[key] || {}), ...(patch[key] || {}) };
  });
  return out;
};

export function mergeFlowSettings(apiSettings, jwtSettings) {
  const jwt = pickThemeSettings(jwtSettings);
  const api = pickThemeSettings(apiSettings);
  const merged = { ...(jwt || {}), ...(api || {}) };
  return Object.keys(merged).length ? merged : null;
}

export function mergeAntdTheme(base, patch) {
  if (!patch) return base;
  return {
    ...base,
    token: { ...(base.token || {}), ...(patch.token || {}) },
    components: mergeComponentSections(base.components, patch.components),
  };
}

export function buildClientThemeFromSettings(settings) {
  if (!settings || typeof settings !== "object") return null;
  settings = normalizeSettingsKeys(settings);

  const primary = pickColor(settings, "primaryColor");
  const secondary = pickColor(settings, "secondaryColor");
  const text = pickColor(settings, "textColor");
  const background = pickColor(settings, "bgColor");
  const buttonText = pickColor(settings, "buttonTextColor");
  const secondaryText = pickThemeColor(settings, "secondaryTextColor", "secondary_text_color");
  const heading = pickThemeColor(settings, "headingColor", "heading_color");
  const link = pickThemeColor(settings, "linkColor", "link_color");
  const surface = pickThemeColor(settings, "surfaceColor", "surface_color");
  const surfaceMuted = pickThemeColor(settings, "surfaceMutedColor", "surface_muted_color");
  const border = pickThemeColor(settings, "borderColor", "border_color");
  const borderRadius = pickNumber(settings, "borderRadius");
  const fontFamily = pickString(settings, "fontFamily");
  const footerLogoUrl = normalizeLogo(settings.logo);

  const accent = primary || secondary;
  const controlSurface = surface || background;
  const outlineText = secondaryText || accent || text;
  const outlineBorder = accent || outlineText;
  const linkColor = link || accent;
  const primaryText = buttonText || text;

  if (
    !accent &&
    !text &&
    !background &&
    !buttonText &&
    !secondaryText &&
    !heading &&
    !link &&
    !surface &&
    !surfaceMuted &&
    !border &&
    borderRadius == null &&
    !fontFamily &&
    !footerLogoUrl
  ) {
    return null;
  }

  const onlyDefaultValues =
    (!primary || primary === DEFAULT_THEME.primaryColor) &&
    (!secondary || secondary === DEFAULT_THEME.secondaryColor) &&
    (!text || text === DEFAULT_THEME.textColor) &&
    (!background || background === DEFAULT_THEME.bgColor) &&
    (!buttonText || buttonText === DEFAULT_THEME.buttonTextColor) &&
    (!secondaryText || secondaryText === DEFAULT_THEME.secondaryTextColor) &&
    (!heading || heading === DEFAULT_THEME.headingColor) &&
    (!link || link === DEFAULT_THEME.linkColor) &&
    (!surface || surface === DEFAULT_THEME.surfaceColor) &&
    (!surfaceMuted || surfaceMuted === DEFAULT_THEME.surfaceMutedColor) &&
    (!border || border === DEFAULT_THEME.borderColor) &&
    (borderRadius == null || borderRadius === DEFAULT_THEME.borderRadius) &&
    isDefaultFontFamily(fontFamily) &&
    !footerLogoUrl;

  if (onlyDefaultValues) {
    return null;
  }

  const token = {};
  if (accent) {
    token.colorPrimary = accent;
    token.colorPrimaryHover = accent;
    token.colorPrimaryActive = accent;
  }
  if (linkColor) {
    token.colorLink = linkColor;
    token.colorLinkHover = linkColor;
    token.colorLinkActive = linkColor;
  }
  if (text) {
    token.colorText = text;
    token.colorTextBase = text;
    token.colorTextHeading = heading || text;
  } else if (heading) {
    token.colorTextHeading = heading;
  }
  if (primaryText) token.colorTextLightSolid = primaryText;
  if (controlSurface) {
    token.colorBgContainer = controlSurface;
    token.colorBgElevated = controlSurface;
    token.colorBgContainerDisabled = controlSurface;
  }
  if (text) token.colorTextDisabled = text;
  if (border) {
    token.colorBorder = border;
    token.colorBorderSecondary = border;
  }
  if (borderRadius != null) {
    token.borderRadius = borderRadius;
    token.borderRadiusLG = borderRadius;
    token.borderRadiusSM = Math.max(4, Math.round(borderRadius / 2));
  }
  if (fontFamily) token.fontFamily = fontFamily;

  const components = {};

  if (accent || outlineText || outlineBorder || primaryText) {
    components.Button = {
      defaultShadow: "none",
      primaryShadow: "none",
      dangerShadow: "none",
      ...(accent && {
        colorPrimary: accent,
        colorPrimaryHover: accent,
        colorPrimaryActive: accent,
        defaultHoverBg: accent,
        defaultHoverBorderColor: accent,
        defaultActiveBg: accent,
        defaultActiveBorderColor: accent,
      }),
      ...(outlineText && { defaultColor: outlineText }),
      ...(outlineBorder && { defaultBorderColor: outlineBorder }),
      ...(primaryText && {
        primaryColor: primaryText,
        defaultHoverColor: primaryText,
        defaultActiveColor: primaryText,
      }),
      defaultBg: "transparent",
    };
  }

  const fieldComponents = {};
  if (accent) {
    fieldComponents.activeBorderColor = accent;
  }
  if (border) fieldComponents.hoverBorderColor = border;
  if (controlSurface) {
    fieldComponents.colorBgContainer = controlSurface;
    fieldComponents.colorBgContainerDisabled = controlSurface;
  }
  if (text) fieldComponents.colorTextDisabled = text;
  if (border) fieldComponents.colorBorder = border;

  if (Object.keys(fieldComponents).length) {
    components.Input = fieldComponents;
    components.InputNumber = fieldComponents;
    components.DatePicker = fieldComponents;
    components.Select = {
      ...fieldComponents,
      ...(controlSurface && { selectorBg: controlSurface }),
    };
  }

  if (controlSurface || border) {
    components.Card = {
      ...(controlSurface && { colorBgContainer: controlSurface }),
      ...(border && { colorBorder: border }),
    };
    components.Modal = {
      ...(controlSurface && {
        contentBg: controlSurface,
        headerBg: controlSurface,
      }),
    };
  }

  if (accent) {
    components.Checkbox = { colorPrimaryHover: accent };
    components.Switch = { colorPrimaryHover: accent };
    components.Slider = {
      handleActiveColor: accent,
      trackBg: accent,
      trackHoverBg: accent,
    };
    components.Spin = { colorPrimary: accent };
    components.Tabs = {
      itemHoverColor: accent,
      itemActiveColor: accent,
      inkBarColor: accent,
    };
    components.Steps = { colorPrimary: accent, colorIcon: accent };
  }

  if (borderRadius != null) {
    const radiusPatch = {
      borderRadius,
      borderRadiusLG: borderRadius,
      borderRadiusSM: Math.max(4, Math.round(borderRadius / 2)),
    };
    components.Button = { ...(components.Button || {}), ...radiusPatch };
    components.Input = { ...(components.Input || {}), ...radiusPatch };
    components.InputNumber = { ...(components.InputNumber || {}), ...radiusPatch };
    components.Select = { ...(components.Select || {}), ...radiusPatch };
    components.DatePicker = { ...(components.DatePicker || {}), ...radiusPatch };
    components.Card = {
      ...(components.Card || {}),
      borderRadius,
      borderRadiusLG: borderRadius,
    };
    components.Modal = { ...(components.Modal || {}), borderRadiusLG: borderRadius };
    components.Tag = { borderRadiusSM: Math.max(4, Math.round(borderRadius / 2)) };
  }

  const cssVariables = {};
  if (accent) {
    cssVariables["--flow-accent"] = accent;
    cssVariables["--flow-accent-hover"] = accent;
    cssVariables["--flow-accent-focus-ring"] = accent;
  }
  if (buttonText) cssVariables["--flow-button-text"] = buttonText;
  if (text) {
    cssVariables["--flow-text"] = text;
    cssVariables["--flow-text-muted"] = text;
  }
  if (outlineText) cssVariables["--flow-secondary-text"] = outlineText;
  if (heading || text) cssVariables["--flow-heading"] = heading || text;
  if (linkColor) {
    cssVariables["--flow-link"] = linkColor;
    cssVariables["--flow-link-hover"] = linkColor;
  }
  if (outlineBorder) {
    cssVariables["--flow-border-accent"] = outlineBorder;
    if (accent) cssVariables["--flow-border-accent-hover"] = accent;
  }
  if (background) cssVariables["--flow-background"] = background;
  if (controlSurface) cssVariables["--flow-surface"] = controlSurface;
  if (surfaceMuted) {
    cssVariables["--flow-surface-muted"] = surfaceMuted;
    cssVariables["--flow-accent-muted"] = surfaceMuted;
  }
  if (border) {
    cssVariables["--flow-border"] = border;
    cssVariables["--flow-border-hover"] = border;
    cssVariables["--flow-border-disabled"] = border;
  }
  if (borderRadius != null) {
    cssVariables["--flow-radius-md"] = `${borderRadius}px`;
    cssVariables["--flow-radius-lg"] = `${borderRadius}px`;
    cssVariables["--flow-radius-xl"] = `${borderRadius + 4}px`;
  }
  if (fontFamily) cssVariables["--flow-font-family"] = fontFamily;

  const layoutShellStyle = {};
  if (background) layoutShellStyle.backgroundColor = background;
  if (text) layoutShellStyle.color = text;
  if (border) layoutShellStyle.borderColor = border;
  if (fontFamily) layoutShellStyle.fontFamily = fontFamily;

  return {
    footerLogoUrl,
    layoutShellStyle,
    cssVariables,
    antdTheme: { token, components },
  };
}
